import MenuSelect from "../sudokit/components/SudoSelect/MenuSelect";
import { CURRENT_AGENT_VERSION, SELECTABLE_AGENT_RUNTIMES, isSelectableRuntime, runtimeLabel, runtimeDescription } from "~shared/agentRuntimeRegistry";
import React, { useCallback, useEffect, useState } from "react";
import Auth from "../Auth";
import { SudoButton } from "../sudokit/components/SudoButton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../sudokit/components/Dialog";

type DefaultPolicy = {
  id: string;
  revision: number;
  include_existing_chats?: boolean;
  runtime: string;
  created_at: string;
  created_by: string;
  reason: string;
  rolled_back_at: string | null;
  rolled_back_by: string | null;
  rollback_reason: string | null;
};
function getRuntimeLabel(runtime: string): string {
  if (runtime === "evolving-v1") return "v2 · Evolving intents";
  if (runtime === "v2.3-kimik-2.6") return "v2.3 · Kimi K2.6";
  if (runtime === "v2.2") return "v2.2 · Luna";
  if (runtime === "master-v1") return "v1 · Original agent";
  return runtimeLabel(runtime);
}

type PendingPolicyChange =
  | { action: "set"; expectedPolicyId: string | null; runtime: string; includeExistingChats: boolean }
  | { action: "rollback"; id: string };

/** Permanent policy applies outside eligible experiments, according to its explicit chat scope. */
export default function ChatDefaultPolicy() {
  const [policies, setPolicies] = useState<DefaultPolicy[]>([]);
  const [hasLoadedPolicies, setHasLoadedPolicies] = useState(false);
  const [selectedRuntime, setSelectedRuntime] = useState<string>(CURRENT_AGENT_VERSION);
  const [includeExistingChats, setIncludeExistingChats] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [pendingChange, setPendingChange] = useState<PendingPolicyChange | null>(null);
  const loadPolicies = useCallback(async () => {
    // Never present a previous snapshot as current after a failed refresh.
    setHasLoadedPolicies(false);
    const result = await Auth.fetchWithIdToken("/api/admin/chat-default-policy/list", {});
    if (!result.success) throw new Error(result.message || "Unable to load default policy");
    const loadedPolicies: DefaultPolicy[] = result.policies ?? [];
    const activePolicy = loadedPolicies[0]?.rolled_back_at ? undefined : loadedPolicies[0];
    setPolicies(loadedPolicies);
    // Editing starts from the saved scope, not an unchecked default that could
    // accidentally stop an ongoing existing-chat migration on the next save.
    setIncludeExistingChats(activePolicy?.include_existing_chats === true);
    setSelectedRuntime(isSelectableRuntime(activePolicy?.runtime) ? activePolicy.runtime : CURRENT_AGENT_VERSION);
    setHasLoadedPolicies(true);
    setError("");
  }, []);
  useEffect(() => {
    loadPolicies().catch((e) => setError(e.message));
  }, [loadPolicies]);
  const currentPolicy = policies[0];
  const effectiveRuntime =
    currentPolicy && !currentPolicy.rolled_back_at && isSelectableRuntime(currentPolicy.runtime) ? currentPolicy.runtime : CURRENT_AGENT_VERSION;
  const effectiveIncludesExisting = !currentPolicy?.rolled_back_at && currentPolicy?.include_existing_chats === true;
  async function confirmPolicyChange() {
    if (!pendingChange) return;
    setIsSaving(true);
    setError("");
    setNotice("");
    try {
      const result = await Auth.fetchWithIdToken("/api/admin/chat-default-policy/change", {
        ...pendingChange,
        reason,
      });
      if (!result.success) throw new Error(result.message || "Unable to save default policy");
      setPendingChange(null);
      setReason("");
      setNotice("Default policy saved.");
      try {
        await loadPolicies();
      } catch {
        setError(
          "The change was saved, but the current policy could not be refreshed. Use Refresh default to reload it.",
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsSaving(false);
    }
  }
  return (
    <section className="my-6 space-y-4 rounded-xl border border-app-menu-color-hover bg-app-menu-color p-6 shadow-card">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Permanent runtime policy</h2>
        <SudoButton
          disabled={isSaving}
          onClick={() => loadPolicies().catch((e) => setError(e.message))}
        >
          Refresh default
        </SudoButton>
      </div>
      {error && (
        <p
          role="alert"
          className="text-app-danger-color"
        >
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {hasLoadedPolicies ? (
        <>
          <p>
            Current default: <strong>{getRuntimeLabel(effectiveRuntime)}</strong>
          </p>
          <p>Current scope: {effectiveIncludesExisting ? "New and existing chats" : "New chats only"}</p>
          <p>
            Eligible experiments take priority. Otherwise, chats receive this default on their first
            user message; an onboarding opener alone does not count as history. Existing
            conversations keep their cohort unless inclusion is explicitly enabled below. Retired v1 and v2 assignments always execute the stable runtime.
          </p>
          <div className="space-y-2 text-sm font-medium">
            <p>Runtime</p>
            <MenuSelect label="Permanent runtime" variant="secondary" value={selectedRuntime} onChange={setSelectedRuntime}
              options={SELECTABLE_AGENT_RUNTIMES.map(runtime => ({ value: runtime, label: runtimeLabel(runtime), subLabel: runtimeDescription(runtime) }))} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={includeExistingChats} onChange={(e) => setIncludeExistingChats(e.target.checked)} />
            Include existing chats on their next turn
          </label>
          <SudoButton
            variant="primary"
            disabled={isSaving || (
              !!currentPolicy && !currentPolicy.rolled_back_at
              && selectedRuntime === currentPolicy.runtime
              && includeExistingChats === effectiveIncludesExisting
            )}
            onClick={() => {
              setReason("");
              setPendingChange({
                action: "set",
                runtime: selectedRuntime,
                includeExistingChats,
                expectedPolicyId: currentPolicy?.id ?? null,
              });
            }}
          >
            Change permanent policy
          </SudoButton>
          <p className="text-sm text-app-grey-1">
            Including existing chats supersedes old assignments outside applicable experiments on their next turn. To move chats already enrolled
            under a default back to the stable runtime, roll back that revision below. Experiments have their own
            rollback.
          </p>
          <details>
            <summary className="cursor-pointer font-medium">Default history and rollback</summary>
            {!policies.length && <p>No changes yet. The stable evolving agent is the default.</p>}
            {policies.map((policy) => (
              <div
                key={policy.id}
                className="my-4 space-y-2 border-t border-app-menu-color-hover pt-4"
              >
                <p>
                  Revision {policy.revision} · {policy.include_existing_chats ? "New and existing chats" : "New chats only"} · {getRuntimeLabel(policy.runtime)} ·{" "}
                  {new Date(policy.created_at).toLocaleString()}
                </p>
                <p>
                  {policy.reason} · {policy.created_by}
                </p>
                {policy.rolled_back_at ? (
                  <p>
                    Rolled back {new Date(policy.rolled_back_at).toLocaleString()} ·{" "}
                    {policy.rollback_reason} · {policy.rolled_back_by}
                  </p>
                ) : (
                  !["master-v1", "v1"].includes(policy.runtime) && (
                    <SudoButton
                      variant="danger"
                      disabled={isSaving}
                      onClick={() => {
                        setReason("");
                        setPendingChange({ action: "rollback", id: policy.id });
                      }}
                    >
                      Roll back revision {policy.revision} to stable
                    </SudoButton>
                  )
                )}
              </div>
            ))}
          </details>
        </>
      ) : (
        !error && <p>Loading default policy…</p>
      )}
      <Dialog
        open={!!pendingChange}
        onOpenChange={(open) => {
          if (!open && !isSaving) setPendingChange(null);
        }}
      >
        {pendingChange && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {pendingChange.action === "set"
                  ? "Change permanent policy"
                  : "Roll back enrolled chats"}
              </DialogTitle>
              <DialogDescription>
                {pendingChange.action === "set"
                  ? `Use ${getRuntimeLabel(pendingChange.runtime)} outside applicable experiments. ${pendingChange.includeExistingChats ? "Existing chats also switch on their next turn, replacing older assignments." : "Existing assignments stay unchanged."}`
                  : "Chats assigned under this revision use the stable runtime on their next turn. Running turns and queued work finish. If this is the current default, new chats also use the stable runtime. Other revisions and experiments are unaffected."}
              </DialogDescription>
            </DialogHeader>
            <label className="flex flex-col gap-2">
              Reason
              <textarea
                className="rounded-md border border-app-grey-3 bg-transparent p-2"
                value={reason}
                maxLength={1000}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            {error && <p role="alert">{error}</p>}
            <DialogFooter>
              <SudoButton
                disabled={isSaving}
                onClick={() => setPendingChange(null)}
              >
                Cancel
              </SudoButton>
              <SudoButton
                variant="primary"
                disabled={isSaving || !reason.trim()}
                onClick={confirmPolicyChange}
              >
                {isSaving ? "Saving…" : "Confirm"}
              </SudoButton>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </section>
  );
}
